import type { ServiceDiagnosticsResponse } from '@web/vin-sessions/types';

/** The three tabs, in the order they appear. Sent to the API upper-cased. */
export const SD_SEARCH_TYPES = ['ALL', 'RECENT', 'FAVORITE'] as const;

export type SdCarlineOption = {
    description: string;
    id: string;
};

export type SdFilters = {
    carlineId?: string;
    modelYear?: string;
};

export type SdSearchType = (typeof SD_SEARCH_TYPES)[number];

export const isSdSearchType = (value: unknown): value is SdSearchType =>
    typeof value === 'string' && (SD_SEARCH_TYPES as ReadonlyArray<string>).includes(value);

export const toModelYearOptions = (diagnostics: ServiceDiagnosticsResponse): string[] =>
    Object.entries(diagnostics)
        .filter(([, carlines]) => carlines.length > 0)
        .map(([year]) => year)
        .sort((a, b) => Number(b) - Number(a));

export const toCarlineOptions = (
    diagnostics: ServiceDiagnosticsResponse,
    modelYear: string | undefined
): SdCarlineOption[] => {
    if (!modelYear) {
        return [];
    }
    return [...(diagnostics[modelYear] ?? [])]
        .map((diagnostic) => ({ id: diagnostic.carlineId, description: diagnostic.carlineDescription }))
        .sort((a, b) => a.description.localeCompare(b.description, undefined, { numeric: true, sensitivity: 'base' }));
};

export const pruneCarline = (diagnostics: ServiceDiagnosticsResponse, filters: SdFilters): SdFilters => {
    if (!filters.carlineId) {
        return filters;
    }
    const stillOffered = toCarlineOptions(diagnostics, filters.modelYear).some(
        (carline) => carline.id === filters.carlineId
    );
    return stillOffered ? filters : { ...filters, carlineId: undefined };
};

export const countSdFilters = (filters: SdFilters): number => (filters.modelYear ? 1 : 0) + (filters.carlineId ? 1 : 0);

export const shouldResetQuery = (text: string, appliedQuery: string): boolean => !text.trim() && Boolean(appliedQuery);

export type SdSort = {
    column: SdSortColumn;
    direction: 'asc' | 'desc';
};

/** Columns the manual list can be ordered by. The API falls back to title for anything else. */
export type SdSortColumn = 'createdAt' | 'title' | 'updatedAt';

export const DEFAULT_SD_SORT: SdSort = { column: 'title', direction: 'asc' };

export const nextSort = (current: SdSort, column: SdSortColumn): SdSort =>
    current.column === column
        ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'asc' };
